import { ApprovalDialogButton } from '@components/Forms';
import DeleteDialogButton from '@components/Forms/DeleteDialog/DeleteDialogButton';
import { LoadingButton } from '@mui/lab';
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	Typography,
	useTheme
} from '@mui/material';
import { ITheme } from 'types/theme';
import { onSubmitForm } from '@utils/forms';
import { AuthMethodRoute } from 'constants/routes';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import React, { FC, useCallback, useMemo, useState } from 'react';
import {
	ConnectorActions,
	ConnectorIndexEnum,
	ConnectorTypeEnum,
	CustomAuthConnector,
	ITestServicePayloadMethodProps,
	ProviderPrefix
} from 'types/connectors';
import {
	getConnectorForm,
	getConnectorFormValidations,
	getConnectorIcon
} from './ConnectorEditDialogUtils';
import ConnectorTest from './ConnectorTest';

interface IConnectorEditProps {
	open: boolean;
	pageId: AuthMethodRoute;
	connector: CustomAuthConnector;
	onClose: () => void;
	onSave: (connector: CustomAuthConnector) => Promise<any>;
	onDelete: (connector: CustomAuthConnector) => void;
	onTest: (props: ITestServicePayloadMethodProps) => Promise<unknown>;
}

const ConnectorEditDialog: FC<IConnectorEditProps> = (props) => {
	const theme = useTheme<ITheme>();

	const { open, pageId, connector, onClose, onSave, onDelete, onTest } = props;
	const currentAction = connector.common?.id
		? ConnectorActions.UPDATE
		: ConnectorActions.CREATE;

	const { addToast } = useToast();
	const [loading, setLoading] = useState(false);
	const [action, setAction] = useState<ConnectorActions | null>(currentAction);
	const values = useMemo<CustomAuthConnector>(() => connector, [connector]);
	const formik = useFormik<CustomAuthConnector>({
		initialValues: values,
		enableReinitialize: true,
		validationSchema: getConnectorFormValidations(connector),
		onSubmit: onSubmitForm<CustomAuthConnector>(
			onSave,
			addToast,
			`connectors.${action}.toast.success`,
			`connectors.${action}.toast.error`
		)
	});

	const onDialogClose = () => {
		setAction(null);
		setLoading(false);
		formik.resetForm();
		onClose();
	};

	const {
		providerDisplayName: connectorName,
		providerType,
		providerProtocol
	} = connector;

	const onConnectorDelete = useCallback(async () => {
		setAction(ConnectorActions.DELETE);
		onDelete(formik.values);
	}, [formik.values, onDelete]);

	const label = intl.tr(
		`connectors.edit.dialog.title.${providerType}.${providerProtocol}.${
			connector.common?.id ? 'edit' : 'new'
		}`
	);
	return (
		<Dialog open={open} onClose={() => onDialogClose()} maxWidth="xs">
			<DialogTitle data-testid="connector-edit-dialog-title">
				<Box p={1}>
					<Grid container direction="row">
						<Grid item xs={1} display="flex" alignSelf="center">
							<img
								src={getConnectorIcon(
									connector.providerProtocol as ConnectorIndexEnum,
									theme
								)}
								alt={label}
								data-testid={`connector-icon-${connector.providerProtocol}`}
							/>
						</Grid>
						<Grid item xs={11}>
							<Typography variant="h6">{label}</Typography>
						</Grid>
					</Grid>
				</Box>
			</DialogTitle>
			<DialogContent data-testid="connector-edit-dialog-content">
				<Box p={1}>
					<form>{getConnectorForm(connector, formik)}</form>
				</Box>
				<Box p={1}>
					<Grid item xs={12}>
						<Divider />
					</Grid>
				</Box>
				<Box display="flex" flexDirection="row" flexGrow={1} padding={1}>
					<ConnectorTest
						type={connector.providerType}
						gridSpacing={2}
						disabled={!formik.isValid}
						onSubmit={async (target, onComplete, onFail) => {
							await onTest({
								isAdhoc: true,
								providerType:
									connector.providerType === ConnectorTypeEnum.EMAIL_PROVIDERS
										? ProviderPrefix.EMAIL
										: ProviderPrefix.SMS,
								providerProtocol: connector.providerProtocol,
								providerId: `${providerProtocol}:${connector.common?.id}`,
								values: formik.values as CustomAuthConnector,
								pageId,
								target,
								onComplete,
								onFail
							});
						}}
					/>
				</Box>
				<Box p={1}>
					<Grid item xs={12}>
						<Divider />
					</Grid>
				</Box>
				<Box display="flex" flexDirection="row" flexGrow={1}>
					<Grid
						container
						direction="row"
						justifyContent="flex-start"
						alignItems="center"
					>
						{connector.common?.id && (
							<Grid item>
								<DeleteDialogButton
									onDelete={onConnectorDelete}
									buttonText={intl.tr('connectors.edit.dialog.button.delete')}
									title={intl.tr(
										`connectors.delete.popup.title.${providerType}`
									)}
									question={intl.tr(
										`connectors.delete.popup.question.${providerType}`,
										{
											connectorName
										}
									)}
									extraVerification={false}
									color="error"
								/>
							</Grid>
						)}
					</Grid>
					<Grid
						container
						direction="row"
						justifyContent="flex-end"
						alignItems="center"
					>
						{formik.dirty ? (
							<ApprovalDialogButton
								buttonText={intl.tr('button.cancel')}
								onApprove={onDialogClose}
								title={intl.tr('discard.changes.dialog.title')}
								question={intl.tr('discard.changes.dialog.question')}
								approveText={intl.tr('discard.changes.dialog.approve')}
							/>
						) : (
							<Button
								onClick={() => onDialogClose()}
								data-testid="connector-create-cancel-button"
							>
								{intl.tr('button.cancel')}
							</Button>
						)}
						<LoadingButton
							color="primary"
							variant="contained"
							loading={loading}
							disabled={!formik.dirty || !formik.isValid}
							sx={{ marginLeft: 1 }}
							onClick={() => {
								setLoading(true);
								formik.handleSubmit();
							}}
							data-testid="connector-create-submit-button"
						>
							{intl.tr(`button.${connector.common?.id ? 'save' : 'create'}`)}
						</LoadingButton>
					</Grid>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default ConnectorEditDialog;
